import React, { useState, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import Box from '@hitagi/core/Box';
import Material from '@hitagi/core/Material';
import UpIcon from '@hitagi/icons/ChevronUp';
import DownIcon from '@hitagi/icons/ChevronDown';
import Text from '@hitagi/core/Text';
import { DotaContext } from '@hitagi/dota/Context';
import { getPickRate } from '../Matches/Drafts/DraftsHeroSummary';
import Row from './TableRow';
import Stat from './TableStat';
import { Header } from './styled';
import { sortByName } from './helpers';

const renderHero = (hero, matchCount) => {
  const {
    heroId,
    matchCountWith,
    matchCountAgainst,
  } = hero;

  const winRateWith = getPickRate(matchCount, matchCountWith) || 0;
  const winRateAgainst = getPickRate(matchCount, matchCountAgainst) || 0;

  return (
    <Row key={heroId} heroId={heroId}>
      <Stat
        stat={matchCountWith}
        percent={winRateWith}
      />
      <Stat
        stat={matchCountAgainst}
        percent={winRateAgainst}
      />
    </Row>
  );
};

const ascUs = (heroA, heroB) => heroA.matchCountWith - heroB.matchCountWith;
const descUs = (heroA, heroB) => heroB.matchCountWith - heroA.matchCountWith;
const ascOpponent = (heroA, heroB) => heroA.matchCountAgainst - heroB.matchCountAgainst;
const descOpponent = (heroA, heroB) => heroB.matchCountAgainst - heroA.matchCountAgainst;

const icons = {
  asc: <UpIcon height="0.8em" />,
  desc: <DownIcon height="0.8em" />,
};

const TablePick = ({ heroes: heroesProp, matchCount, ...restProps }) => {
  const heroes = useMemo(() => heroesProp.slice().sort(descUs), [heroesProp]);

  const [sortName, setSortName] = useState('');
  const [sortUs, setSortUs] = useState('desc');
  const [sortOpponent, setSortOpponent] = useState('');
  const dota = useContext(DotaContext);

  const onClickUs = () => {
    sortByName(heroes, dota);
    setSortName('');
    setSortOpponent('');
    setSortUs(prev => {
      if (prev === '') {
        heroes.sort(descUs);
        return 'desc';
      }
      if (prev === 'desc') {
        heroes.sort(ascUs);
        return 'asc';
      }
      if (prev === 'asc') {
        heroes.sort(descUs);
        return 'desc';
      }
      return '';
    });
  };

  const onClickOpponent = () => {
    sortByName(heroes, dota);
    setSortName('');
    setSortUs('');
    setSortOpponent(prev => {
      if (prev === '') {
        heroes.sort(descOpponent);
        return 'desc';
      }
      if (prev === 'desc') {
        heroes.sort(ascOpponent);
        return 'asc';
      }
      if (prev === 'asc') {
        heroes.sort(descOpponent);
        return 'desc';
      }
      return '';
    });
  };

  const onClickTitle = () => {
    setSortUs('');
    setSortOpponent('');
    setSortName(prev => {
      if (prev === '') {
        sortByName(heroes, dota);
        return 'desc';
      }
      if (prev === 'desc') {
        sortByName(heroes, dota, 'asc');
        return 'asc';
      }
      if (prev === 'asc') {
        sortByName(heroes, dota);
        return 'desc';
      }
      return '';
    });
  };

  return (
    <Box width="100%" marginRight={1} {...restProps}>
      <Header>
        <Box as="span" onClick={onClickTitle}>
          <Text variant="head2" as="span">Pick</Text> {icons[sortName]}
        </Box>
        <Box flexGrow={1} />
        <Box width="9ch" as="span" onClick={onClickUs}>
          Us {icons[sortUs]}
        </Box>
        <Box width="12ch" as="span" onClick={onClickOpponent}>
          Opponent {icons[sortOpponent]}
        </Box>
      </Header>
      <Material padding={0} component="ul">
        {heroes.map(hero => renderHero(hero, matchCount))}
      </Material>
    </Box>
  );
};

TablePick.propTypes = {
  matchCount: PropTypes.number,
  heroes: PropTypes.array, // eslint-disable-line react/forbid-prop-types
};

export default TablePick;
